import React, { useState, useContext, useEffect } from 'react';
import _ from 'lodash';
import cn from 'classnames';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faVideo } from '@fortawesome/pro-regular-svg-icons';
import Tooltip from '../General/Tooltip';
import './OfficeSpacePopover.scss';

import { OfficeContext } from './Office';
import { createOfficeBooking, deleteOfficeBooking, getOfficeBookingsMeetings } from '../../api/office';
import { getSmartImage } from '../../helpers/helpers';
import {
  SPACE_TYPE_LABELS,
  OFFICE_DOOR_STATES,
  SPACE_TYPE_DEFAULTS,
  canBookSpace,
  SLOT_MINUTES,
  TOTAL_SLOTS,
  slotToTimeStr,
  timeToSlot,
  formatTimeClean,
  inferDeskPeriod,
  getOfficePermissions,
  isRoomType,
  isOfficeAdminOrExec,
  isBlackoutDate
} from '../../helpers/office_helpers';

const OfficeSpacePopover = ({ space, onClose, positionStyle, className: extraClass }) => {
  const ctx = useContext(OfficeContext);
  const {
    currentUser,
    selectedDate,
    selectedDateAnnouncement,
    selectedTime,
    selectedEndTime,
    setSelectedTime,
    setSelectedEndTime,
    selectedDayPossessive,
    onBookingChange,
    myBookings,
    isAdmin
  } = ctx;
  const [isBooking, setIsBooking] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [bookingTitle, setBookingTitle] = useState('');
  const [reserveForSomeoneElse, setReserveForSomeoneElse] = useState(false);
  const [doorState, setDoorState] = useState(OFFICE_DOOR_STATES.OPEN);
  const [deskPeriod, setDeskPeriod] = useState(() => inferDeskPeriod(selectedTime, selectedEndTime));

  const [isAddingToMeeting, setIsAddingToMeeting] = useState(false);
  const [existingMeetings, setExistingMeetings] = useState([]);
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);
  const [isLoadingMeetings, setIsLoadingMeetings] = useState(false);

  useEffect(() => {
    setBookingTitle('');
    setReserveForSomeoneElse(false);
    setDoorState(OFFICE_DOOR_STATES.OPEN);
    setIsBooking(false);
    setIsAddingToMeeting(false);
    setExistingMeetings([]);
    setSelectedMeetingId(null);
  }, [space?.id]);

  useEffect(() => {
    setDeskPeriod(inferDeskPeriod(selectedTime, selectedEndTime));
  }, [selectedTime, selectedEndTime]);

  if (!space) return null;

  const typeLabel = SPACE_TYPE_LABELS[space.type] || space.type;
  const hasDoor = SPACE_TYPE_DEFAULTS[space.type]?.has_door;
  const isDeskType = space.type === 'desk' || space.type === 'open_seating';
  const canReserveForSomeoneElse = isDeskType && isAdmin;
  const userCanBook = canBookSpace(currentUser, space);
  const zoomParticipants = ctx.zoomParticipants?.[space?.id];

  const typeDefaults = SPACE_TYPE_DEFAULTS[space.type];
  const defaultDuration = typeDefaults?.default_duration_min || 30;
  const defaultSlots = defaultDuration / SLOT_MINUTES;

  // Compute booking window from global time bar
  let bookingStart, bookingEnd;
  if (isDeskType) {
    bookingStart = deskPeriod === 'pm' ? `${selectedDate}T13:00:00` : `${selectedDate}T07:00:00`;
    bookingEnd = deskPeriod === 'am' ? `${selectedDate}T13:00:00` : `${selectedDate}T21:00:00`;
  } else if (selectedEndTime) {
    bookingStart = `${selectedDate}T${selectedTime}:00`;
    bookingEnd = `${selectedDate}T${selectedEndTime}:00`;
  } else {
    const startSlot = Math.min(timeToSlot(selectedTime), TOTAL_SLOTS - defaultSlots);
    const endSlot = startSlot + defaultSlots;
    bookingStart = `${selectedDate}T${slotToTimeStr(Math.round(startSlot))}:00`;
    bookingEnd = `${selectedDate}T${slotToTimeStr(Math.round(endSlot))}:00`;
  }

  const now = moment();
  const selStartMoment = moment(bookingStart);
  const selEndMoment = moment(bookingEnd);
  const isInPast = selEndMoment.isBefore(now);

  const bookings = (space.bookings || []).filter(b => b.status === 'confirmed' && moment(b.ends_at).isAfter(moment.min(now, selStartMoment)));
  const liveBooking = bookings.filter(b => {
    const bStart = moment(b.starts_at);
    const bEnd = moment(b.ends_at);
    return now.isAfter(bStart) && now.isBefore(bEnd);
  })[0];

  const conflictingBookings = bookings.filter(b => {
    const bStart = moment(b.starts_at);
    const bEnd = moment(b.ends_at);
    return (
      selStartMoment.isBetween(bStart, bEnd, undefined, '[)') ||
      selEndMoment.isBetween(bStart, bEnd, undefined, '(]') ||
      bStart.isBetween(selStartMoment, selEndMoment, undefined, '[)') ||
      bEnd.isBetween(selStartMoment, selEndMoment, undefined, '(]')
    );
  });
  const hasConflict = conflictingBookings.length >= (space.max_concurrent_bookings || 1);

  const doesExistingMeetingConflict = meeting => {
    const meetingStart = meeting.start?.dateTime ?? meeting.start?.date;
    const meetingEnd = meeting.end?.dateTime ?? meeting.end?.date;
    if (!meetingStart || !meetingEnd) return false;

    const mStartMoment = moment(meetingStart);
    const mEndMoment = moment(meetingEnd);

    const conflicts = bookings.filter(b => {
      const bStart = moment(b.starts_at);
      const bEnd = moment(b.ends_at);
      return (
        mStartMoment.isBetween(bStart, bEnd, undefined, '[)') ||
        mEndMoment.isBetween(bStart, bEnd, undefined, '(]') ||
        bStart.isBetween(mStartMoment, mEndMoment, undefined, '[)') ||
        bEnd.isBetween(mStartMoment, mEndMoment, undefined, '(]')
      );
    });

    return conflicts.length >= (space.max_concurrent_bookings || 1);
  };

  const hasZoom = (liveBooking && liveBooking.zoom_join_url) || (conflictingBookings.length === 1 && conflictingBookings[0].zoom_join_url);
  const zoomBooking = hasZoom ? liveBooking ?? conflictingBookings?.[0] : undefined;

  const existingDeskBookingToday =
    isDeskType &&
    myBookings?.find(b => {
      const isDesk = b.space?.type === 'desk' || b.space?.type === 'open_seating';
      if (!isDesk) return false;
      return !b.title && b.starts_at && b.starts_at.startsWith(selectedDate);
    });

  const myBooking = conflictingBookings.find(b => b.User_id === currentUser?.id && !(isDeskType && b.title));

  let isOverTimeLimit = false;
  if (isRoomType(space) && !isOfficeAdminOrExec(currentUser)) {
    const newDuration = moment(bookingEnd).diff(moment(bookingStart), 'hours', true);
    const userRoomBookings = (myBookings || []).filter(
      b =>
        b.status === 'confirmed' && b.starts_at && b.starts_at.startsWith(selectedDate) && (b.space?.id === space.id || b.OfficeSpace_id === space.id)
    );
    const currentTotal = userRoomBookings.reduce((acc, b) => acc + moment(b.ends_at).diff(moment(b.starts_at), 'hours', true), 0);
    if (currentTotal + newDuration > 4.01) {
      isOverTimeLimit = true;
    }
  }

  const permissions = getOfficePermissions(currentUser);
  const maxAdvanceDays = permissions?.max_advance_days || 7;
  const isEarlyBooking = moment(selectedDate).diff(moment(), 'days') > maxAdvanceDays;
  const isBlackout = isBlackoutDate(selectedDate);

  const canBook = userCanBook && !hasConflict && !isBooking && !isOverTimeLimit && !isBlackout;
  const hasRequiredReservationTitle = !reserveForSomeoneElse || bookingTitle.trim().length > 0;
  const isReplacingDeskBooking = Boolean(existingDeskBookingToday && !reserveForSomeoneElse);
  const bookingButtonLabel = isBooking
    ? isReplacingDeskBooking
      ? 'Switching...'
      : 'Booking...'
    : isReplacingDeskBooking
    ? 'Change Booking'
    : 'Book';

  const handleAddExistingClick = async () => {
    setIsAddingToMeeting(true);
    setIsLoadingMeetings(true);
    setBookingTitle('');
    try {
      const resp = await getOfficeBookingsMeetings({ date: selectedDate });
      const meetings = (resp?.meetings ?? []).filter(m => moment(m.end.dateTime).isAfter(now));
      setExistingMeetings(meetings);
    } catch (err) {
      console.error(err);
      setExistingMeetings([]);
    } finally {
      setIsLoadingMeetings(false);
    }
  };

  const handleSelectExistingMeeting = m => {
    if (!m.canInvite) return;
    setSelectedMeetingId(m.id);
    setBookingTitle(m.summary || '');
    if (m.start?.dateTime && m.end?.dateTime) {
      setSelectedTime(moment(m.start.dateTime).format('HH:mm'));
      setSelectedEndTime(moment(m.end.dateTime).format('HH:mm'));
    }
  };

  const handleBook = async () => {
    if (!canBook || !hasRequiredReservationTitle) return;

    setIsBooking(true);
    try {
      if (isEarlyBooking) {
        const confirmed = window.confirm(`You are booking early for ${selectedDateAnnouncement.title}. Please only book if you are participating.`);
        if (!confirmed) return;
      }
      if (isReplacingDeskBooking) {
        await deleteOfficeBooking(existingDeskBookingToday.id);
      }
      await createOfficeBooking({
        OfficeSpace_id: space.id,
        title: bookingTitle || null,
        starts_at: bookingStart,
        ends_at: bookingEnd,
        door_state: hasDoor ? doorState : null,
        ...(selectedMeetingId ? { google_calendar_event_id: selectedMeetingId } : {}),
        ...(reserveForSomeoneElse ? { reserve_for_someone_else: true } : {})
      });
      window.ALERT.success(isReplacingDeskBooking ? `Switched to ${space.name}` : `Booked ${space.name}`);
      setBookingTitle('');
      await onBookingChange();
      onClose();
    } catch (err) {
      window.ALERT.error(err || 'Failed to book.');
      if (isReplacingDeskBooking) {
        await onBookingChange();
      }
    } finally {
      setIsBooking(false);
    }
  };

  const handleCancel = async (booking, deleteMeeting) => {
    if (!booking || isCancelling) return;
    setIsCancelling(true);
    if (deleteMeeting) {
      const confirm = window.confirm(`Are you sure you want to delete "${booking.title}" from Google Calendar?`);
      if (!confirm) {
        setIsCancelling(false);
        return;
      }
    }
    try {
      await deleteOfficeBooking(booking.id, deleteMeeting);
      window.ALERT.success('Booking cancelled');
      await onBookingChange();
    } catch (err) {
      window.ALERT.error(err || 'Failed to cancel.');
    } finally {
      setIsCancelling(false);
    }
  };

  let posStyle,
    flipLeft = false,
    flipUp = false;
  if (positionStyle) {
    posStyle = positionStyle;
  } else {
    const cx = (space.floor_plan_x || 0) + (space.floor_plan_w || 0.06);
    const cy = space.floor_plan_y || 0;
    const spaceBottom = cy + (space.floor_plan_h || 0.06);
    flipLeft = cx > 0.65;
    flipUp = spaceBottom > 0.8;
    posStyle = {};
    if (flipLeft) posStyle.right = `${(1 - (space.floor_plan_x || 0)) * 100}%`;
    else posStyle.left = `${cx * 100}%`;
    if (flipUp) posStyle.bottom = `${(1 - cy) * 100}%`;
    else posStyle.top = `${cy * 100}%`;
  }

  return (
    <div
      className={cn('office-space-popover', { 'flip-left': flipLeft, 'flip-up': flipUp, 'custom-pos': !!positionStyle }, extraClass)}
      style={posStyle}
      onClick={e => e.stopPropagation()}
    >
      {space.cover_image && (
        <div className='popover-cover'>
          <img src={getSmartImage(space.cover_image)} alt={space.name} className='cover-img' />
          <button className='popover-close over-cover' onClick={onClose}>
            &times;
          </button>
        </div>
      )}
      <div className='popover-header'>
        <div className='popover-info'>
          <div className='popover-name'>{space.name}</div>
          <div className='popover-meta'>
            {typeLabel} · {space.capacity} {space.capacity === 1 ? 'seat' : 'seats'}
          </div>
        </div>
        <div className='popover-header-actions'>
          {zoomBooking && zoomBooking.door_state !== 'closed' && (
            <a
              href={zoomBooking.zoom_join_url}
              target='_blank'
              rel='noopener noreferrer'
              className='popover-enter-icon'
              title={`Enter ${space.name}`}
            >
              <FontAwesomeIcon icon={faVideo} />
            </a>
          )}
          {!space.cover_image && (
            <button className='popover-close' onClick={onClose}>
              &times;
            </button>
          )}
        </div>
      </div>

      {/* Day schedule */}
      <div className='popover-schedule'>
        <div className='schedule-label'>{selectedDayPossessive} Schedule</div>
        {bookings.length === 0 ? (
          <div className='schedule-free-all'>Open all day</div>
        ) : (
          <div className='schedule-list'>
            {_.sortBy(bookings, 'starts_at').map(b => {
              const hasTitle = isDeskType && Boolean(b.title);
              const displayName = hasTitle ? b.title : b.user?.name || 'Someone';
              const img = hasTitle ? null : getSmartImage(b.user?.image);
              const isConflict = conflictingBookings.some(c => c.id === b.id);
              const isOwnBooking = b.User_id === currentUser?.id;
              const startSlot = timeToSlot(b.starts_at.split(' ')[1]);
              const endSlot = timeToSlot(b.ends_at.split(' ')[1]);
              const middaySlot = timeToSlot('13:00');
              const presetLabel =
                startSlot === 0 && endSlot === TOTAL_SLOTS
                  ? 'Full Day'
                  : startSlot === 0 && endSlot === middaySlot
                  ? 'Morning'
                  : startSlot === middaySlot && endSlot === TOTAL_SLOTS
                  ? 'Afternoon'
                  : undefined;
              return (
                <div key={b.id} className={cn('schedule-item', { conflict: isConflict })}>
                  <div className='schedule-time'>
                    {presetLabel ?? (
                      <>
                        <span className='schedule-start'>{moment(b.starts_at).format('h:mm')}</span>
                        <span className='schedule-dash'>–</span>
                        <span className='schedule-end'>{moment(b.ends_at).format('h:mm A')}</span>
                      </>
                    )}
                  </div>
                  <div className='schedule-person'>
                    {img ? (
                      <img src={img} alt='' className='schedule-face' />
                    ) : hasTitle ? null : (
                      <div className='schedule-face placeholder'>{b.user?.name?.charAt(0) || '?'}</div>
                    )}
                    <span className='schedule-name'>{displayName}</span>
                  </div>
                  {b.zoom_join_url && b.door_state !== 'closed' && (
                    <a href={b.zoom_join_url} target='_blank' rel='noopener noreferrer' className='popover-enter-icon' title={`Join meeting`}>
                      <FontAwesomeIcon icon={faVideo} />
                    </a>
                  )}
                  {isOwnBooking && (
                    <button className='schedule-cancel-btn' onClick={() => handleCancel(b)} disabled={isCancelling} title='Cancel booking'>
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Preview Zoom participants */}
      {Boolean(zoomParticipants) && showJoinZoom && (
        <div className='popover-zoom-participants'>
          <div className='schedule-label'>Currently Meeting</div>
          <ul className='zoom-participants-list'>
            {zoomParticipants.map(p => {
              const teamMember = ctx.allUsers.find(u => u.email === p);
              return (
                <li key={p} className='zoom-participant'>
                  <Tooltip message={teamMember?.name ?? p}>
                    {teamMember ? (
                      <div>
                        <img src={getSmartImage(teamMember.image)} className='zoom-avatar'></img>
                      </div>
                    ) : (
                      <p className='zoom-monogram'>{p[0]}</p>
                    )}
                  </Tooltip>
                </li>
              );
            })}
          </ul>
          <a
            href={space.persistent_zoom_room_url}
            target='_blank'
            rel='noopener noreferrer'
            className='popover-join-btn'
            title={`Enter ${space.name}`}
          >
            Join
          </a>
        </div>
      )}

      {/* Already booked by current user */}
      {myBooking && (
        <div className='popover-my-booking'>
          <div className='my-booking-status'>You're booked</div>
          <div className='my-booking-time'>
            {moment(myBooking.starts_at).format('h:mm A')} – {moment(myBooking.ends_at).format('h:mm A')}
          </div>
          <button className='popover-cancel-btn' onClick={() => handleCancel(myBooking)} disabled={isCancelling}>
            {isCancelling
              ? 'Cancelling...'
              : myBooking.google_calendar_event_id && !existingDeskBookingToday
              ? 'Cancel & Keep Meeting'
              : 'Cancel Booking'}
          </button>
          {myBooking.google_calendar_event_id && !existingDeskBookingToday && (
            <button className='popover-cancel-btn' onClick={() => handleCancel(myBooking, true)} disabled={isCancelling}>
              {isCancelling ? 'Cancelling...' : 'Cancel & Delete Meeting'}
            </button>
          )}
        </div>
      )}

      {/* Booking section — only if user hasn't already booked */}
      {!myBooking && userCanBook && !hasConflict && !isInPast && !isOverTimeLimit && (
        <div className='popover-book-section'>
          <div className='book-section-header'>{isReplacingDeskBooking ? `Switch to ${space.name}` : `Book ${space.name}`}</div>
          {isDeskType ? (
            <>
              <div className='popover-desk-period'>
                <div className='desk-period-options'>
                  {[
                    { key: 'am', label: 'Morning', sub: '7 AM – 1 PM' },
                    { key: 'pm', label: 'Afternoon', sub: '1 – 9 PM' },
                    { key: 'full_day', label: 'Full Day', sub: '7 AM – 9 PM' }
                  ].map(p => (
                    <button key={p.key} className={cn('period-opt', { active: deskPeriod === p.key })} onClick={() => setDeskPeriod(p.key)}>
                      <span className='period-label'>{p.label}</span>
                      <span className='period-sub'>{p.sub}</span>
                    </button>
                  ))}
                </div>
              </div>
              {canReserveForSomeoneElse && (
                <label className='reserve-for-someone-else'>
                  <input
                    type='checkbox'
                    checked={reserveForSomeoneElse}
                    onChange={e => {
                      setReserveForSomeoneElse(e.target.checked);
                      if (!e.target.checked) setBookingTitle('');
                    }}
                  />
                  <span>Reserve for someone else</span>
                </label>
              )}
              {reserveForSomeoneElse && (
                <input
                  className='popover-title-input'
                  type='text'
                  placeholder='Who is this for?'
                  value={bookingTitle}
                  onChange={e => setBookingTitle(e.target.value)}
                />
              )}
            </>
          ) : (
            <>
              <div className='popover-time-label'>
                {`${formatTimeClean(bookingStart)} – ${formatTimeClean(bookingEnd)}`}
                {!selectedEndTime && <span className='time-hint'>{defaultDuration} min</span>}
              </div>
              <input
                className='popover-title-input'
                type='text'
                placeholder={isAddingToMeeting ? 'Search meetings' : 'Meeting name (optional)'}
                value={bookingTitle}
                onChange={e => setBookingTitle(e.target.value)}
              />
            </>
          )}

          {!isAddingToMeeting && (
            <button className='popover-book-btn' onClick={handleBook} disabled={isBooking || !hasRequiredReservationTitle}>
              {bookingButtonLabel}
            </button>
          )}
        </div>
      )}

      {/* Add to existing meeting */}
      <div className='popover-book-section'>
        {!isDeskType &&
          userCanBook &&
          !isOverTimeLimit &&
          (isAddingToMeeting ? (
            <div className='popover-meetings-list'>
              {isLoadingMeetings ? (
                <div className='meetings-loading'>Loading meetings...</div>
              ) : (
                existingMeetings
                  ?.filter(m => !bookingTitle || selectedMeetingId === m.id || m.summary?.toLowerCase().includes(bookingTitle.toLowerCase()))
                  .map(m => {
                    const isConflict = doesExistingMeetingConflict(m);
                    return (
                      <div
                        key={m.id}
                        className={cn('meeting-item', { selected: selectedMeetingId === m.id, disabled: !m.canInvite || isConflict })}
                        onClick={() => !isConflict && handleSelectExistingMeeting(m)}
                      >
                        <div className='meeting-summary'>{m.summary}</div>
                        <div className='meeting-time'>
                          {moment(m.start?.dateTime || m.start?.date).format('h:mm A')} -{moment(m.end?.dateTime || m.end?.date).format('h:mm A')}
                        </div>
                      </div>
                    );
                  })
              )}
              {!isLoadingMeetings && existingMeetings.length === 0 && <div>No meetings found</div>}
              <button
                className='popover-cancel-meeting-btn'
                onClick={() => {
                  setIsAddingToMeeting(false);
                  setBookingTitle('');
                  setSelectedMeetingId(null);
                }}
              >
                Cancel
              </button>
              <button className='popover-book-btn' onClick={handleBook} disabled={isBooking || !hasRequiredReservationTitle}>
                {bookingButtonLabel}
              </button>
            </div>
          ) : (
            <button className='popover-existing-meeting-btn' onClick={handleAddExistingClick}>
              <FontAwesomeIcon icon={faPlus} style={{ width: '8px' }} />
              Add to Existing Meeting
            </button>
          ))}
        {isEarlyBooking && selectedDateAnnouncement && !isOverTimeLimit && (
          <div className='popover-unavailable'>Please only book early if you are participating in the {selectedDateAnnouncement.title}</div>
        )}
      </div>

      {!myBooking && userCanBook && hasConflict ? (
        /* Unavailable — conflict with someone else */
        <div className='popover-unavailable'>Unavailable for this time</div>
      ) : isInPast ? (
        /* Unavailable — in the past */
        <div className='popover-unavailable'>Selected time is in the past</div>
      ) : !myBooking && userCanBook && isBlackout ? (
        /* Unavailable — blackout date */
        <div className='popover-locked'>Office is closed for booking on this date</div>
      ) : !myBooking && userCanBook && isOverTimeLimit ? (
        /* Unavailable — time limit reached */
        <div className='popover-locked'>This room may not be booked for more than 4 hours</div>
      ) : (
        undefined
      )}

      {!myBooking && !userCanBook && <div className='popover-locked'>Requires higher access</div>}
    </div>
  );
};

export default OfficeSpacePopover;
